import { useState, useContext, useCallback, useRef, useEffect } from "react";
import DataTable from "core/ui/data-table";
import FormatDateTime from "core/utils/FormatDateTime";
import FormatNumber from "core/utils/FormatNumber";
import { withRouter } from "react-router-dom";
import SessionContext from "SessionContext";
import {
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Dropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from "reactstrap";
import { formatPopulationDomain } from "./explorer";
import LZString from 'lz-string';

const table_columns = [
  { header: "Audience Name", key: "name" },
  {
    header: "Date Created",
    key: "created_date",
    render: (value) => <FormatDateTime date={value.created_date} />,
  },
  {
    header: "Date Updated",
    key: "updated_date",
    render: (value) => <FormatDateTime date={value.updated_date} />,
  },
  { header: "Created By", key: "created_by" },
  {header: 'Population Domain', key: 'population_domain', render: (value) => formatPopulationDomain(value.population_domain) },
  {
    header: "Count at time of last save",
    key: "count",
    render: (value) => <FormatNumber value={value.count} />,
  },
];

const Audiences = (props) => {
  const context = useContext(SessionContext);
  const [selectedAudience, setSelectedAudience] = useState(null);
  const [deleteModalIsOpen, setDeleteModalIsOpen] = useState(false);
  const [copyModalIsOpen, setCopyModalIsOpen] = useState(false);
  const updateDataFlagRef = useRef(false);
  const getUpdateDataFlag = useCallback(() => updateDataFlagRef.current, []);
  const [pageActionDropDownIsOpen, setPageActionDropDownIsOpen] = useState(false);
  const [pendingCopyAudience, setPendingCopyAudience] = useState(null);
  const [resetPopulation, setResetPopulation] = useState(null);

  const chartsLoaded = context.chartsStore.loaded;
  useEffect(() => {
    if (chartsLoaded && pendingCopyAudience) {
      context.crossfilterStore.save_audience(pendingCopyAudience, false, resetPopulation);
      updateDataFlagRef.current = !updateDataFlagRef.current;
      setCopyModalIsOpen(false);
      setPendingCopyAudience(null);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chartsLoaded, pendingCopyAudience]);

  function onClickNew(path) {
    context.crossfilterStore.setExplorerModule(path);
    localStorage.setItem("am:new_explorer", "yes");
    const zoomLevel = context.crossfilterStore.getDefaultMapZoom();
    context.crossfilterStore.restoreFromAudience("clear_all");
    context.audienceSaved.clearEditingStates();
    localStorage.setItem("am:audience:map:center", JSON.stringify(context.crossfilterStore.companyHq));
    localStorage.setItem("am:audience:map:zoom", zoomLevel);
    localStorage.removeItem("explorer:layers:boundaries");
    setTimeout(() => {
      props.history.push("/explorer");
    }, 300);
  }

  function deleteAudience() {
    context
      .command("audience/delete", { id: selectedAudience.id })
      .then((audience) => {
        context.crossfilterStore.delete_audience(audience);
        updateDataFlagRef.current = !updateDataFlagRef.current;
        setDeleteModalIsOpen(false);
      })
      .catch((error) => {
        setDeleteModalIsOpen(false);
        console.log(JSON.stringify(error));
      });
  }

  function copyAudience() {

    const _current_population = context.crossfilterStore.explorerModule;
    const _audience_population = selectedAudience?.population_domain;
    const last_audience_id = JSON.parse(LZString.decompress(localStorage.getItem("am:audience:audience")) || null);

    // if audience has different population: switch to get charts data
    if(_current_population !== _audience_population) {
      // save explorer audience and population to reset to after copying
      last_audience_id?.id && localStorage.setItem("am:audience:reload", JSON.stringify(last_audience_id?.id));
      setResetPopulation(_current_population);
      _audience_population && context.crossfilterStore.setExplorerModule(selectedAudience.population_domain);
    }

    let curr_date = new Date();
    let date = curr_date.getFullYear().toString() + '/' +
      (curr_date.getMonth()+1).toString().padStart(2,'0') + '/' +
      curr_date.getDate().toString().padStart(2,'0');
    let hours = curr_date.getHours();
    let minutes = curr_date.getMinutes().toString().padStart(2,'0');
    let seconds = curr_date.getSeconds().toString().padStart(2,'0');
    let ampm = hours < 12 ? 'AM' : 'PM';
    let copy_time = date + ' ' +
      hours.toString().padStart(2,'0') + ':' +
      minutes + ':' +
      seconds + ' ' + ampm;

    context
      .command("audience/copy", {
        id: selectedAudience.id,
        copy_time: copy_time,
        population_domain: selectedAudience.population_domain
      })
      .then((audience) => {

        const _chart_ids = Object.keys(audience.filters);

        let has_all_charts = _chart_ids.every((val) => {
          return context.chartsStore[val] != null;
        })

        if(has_all_charts || audience.is_complex) {
          context.crossfilterStore.save_audience(audience, false, _current_population);
          updateDataFlagRef.current = !updateDataFlagRef.current;
          setCopyModalIsOpen(false);
        } else {
          // charts are still loading (e.g. module switch triggered a reload);
          setPendingCopyAudience(audience);
        }
      })
      .catch((error) => {
        setCopyModalIsOpen(false);
        console.log(JSON.stringify(error));
      });
  }

  const onClickEditAudience = useCallback((_audience) => {
    context.crossfilterStore.set_missing_filters(false);
    context.audienceSaved.clearEditingStates();
    // Clear any existing filters and insights as they will get rebuilt.
    context.crossfilterStore.setFilters([]);
    context.crossfilterStore.setWidgets({});
    setTimeout(() => {
      // small timeout to allow react state to settle before navigating away
      props.history.push(`/explorer#audience=${_audience.id}`);
    }, 300);
  }, [context, props.history]);

  const table_actions = useCallback((audience) => (
    <AudienceActions
      audience={audience}
      onCopy={() => { setSelectedAudience(audience); setCopyModalIsOpen(true); }}
      onDelete={() => { setSelectedAudience(audience); setDeleteModalIsOpen(true); }}
      onEditAudience={() => onClickEditAudience(audience)}
      access={context.appState.userCan}
    />
  ), [context, onClickEditAudience]);

  return (
    <>
      <div className="page audiences-page">
        <div className="list-header sticky-page-header">
          <h1>My Audiences</h1>
          {context.appState.userCan('audience_create') && (
            <Dropdown
              className="dd-page-actions"
              isOpen={pageActionDropDownIsOpen}
              toggle={() => setPageActionDropDownIsOpen(v => !v)}
              onMouseEnter={() => setPageActionDropDownIsOpen(true)}
              onMouseLeave={() => setPageActionDropDownIsOpen(false)}
            >
              <DropdownToggle size="sm" caret>
                New Audience
              </DropdownToggle>
              <DropdownMenu>
                <DropdownItem onClick={() => onClickNew('consumers')}>
                  <i className="fa-solid fa-globe population-icon"></i> New Consumers Audience
                </DropdownItem>
                <DropdownItem onClick={() => onClickNew('movers')}>
                  <i className="fa-solid fa-truck population-icon"></i> New Movers Audience
                </DropdownItem>
                <DropdownItem onClick={() => onClickNew('life_events')}>
                  <i className="fa-regular fa-calendar-days population-icon"></i> New Life Events Audience
                </DropdownItem>
              </DropdownMenu>
            </Dropdown>
          )}
        </div>
        <DataTable
          query="audience/list"
          cols={table_columns}
          actions={table_actions}
          empty="No Audiences Found"
          update_data={getUpdateDataFlag}
        />
        {/* Delete Modal */}
        <Modal
          centered
          isOpen={deleteModalIsOpen}
          toggle={() => setDeleteModalIsOpen(v => !v)}
          backdrop
        >
          <ModalHeader toggle={() => setDeleteModalIsOpen(v => !v)}>
            Confirm Deletion of this Audience:
          </ModalHeader>
          <ModalBody>
            <p>
              Note: Deleting audience{" "}
              <strong>{selectedAudience && selectedAudience.name}</strong>{" "}
              will remove it from filters in other audiences.
            </p>
          </ModalBody>
          <ModalFooter>
            <Button onClick={() => setDeleteModalIsOpen(false)}>Cancel</Button>
            <Button className="promote" onClick={() => deleteAudience()}>
              Delete
            </Button>
          </ModalFooter>
        </Modal>
        {/* Copy Modal */}
        <Modal
          centered
          isOpen={copyModalIsOpen}
          toggle={() => setCopyModalIsOpen(v => !v)}
          backdrop
        >
          <ModalHeader toggle={() => setCopyModalIsOpen(v => !v)}>
            Confirm Copy of this Audience:
          </ModalHeader>
          <ModalBody>
            <p>
              The audience{" "}
              <strong>{selectedAudience && selectedAudience.name}</strong>{" "}
              will be copied.
            </p>
          </ModalBody>
          <ModalFooter>
            <Button onClick={() => setCopyModalIsOpen(false)}>Cancel</Button>
            <Button className="promote" onClick={() => copyAudience()}>
              Copy
            </Button>
          </ModalFooter>
        </Modal>
      </div>
    </>
  );
};
export default withRouter(Audiences);

function AudienceActions({
  audience,
  onCopy,
  onDelete,
  onEditAudience,
  access
} = {}) {
  const session = useContext(SessionContext);

  function editAudience() {
    session.crossfilterStore.setExplorerModule(audience.population_domain);
    onEditAudience();
  }

  return (
    <>
    <ul id="action-menu" className="action-menu">
      {access('audience_edit') &&
        <li>
          <Button color="link" size="sm" onClick={editAudience}>
            <i className="bi bi-pencil" />Edit
          </Button>
        </li>
      }
      {(!access('audience_edit') && access('audience_view')) &&
        <li>
          <Button color="link" size="sm" onClick={editAudience}>
            <i className="bi bi-eye" />View
          </Button>
        </li>
      }
      {access('audience_create') &&
        <li>
          <Button color="link" size="sm" onClick={onCopy}>
            <i className="bi bi-file-earmark-plus" />Copy
          </Button>
        </li>
      }
      {access('audience_delete') &&
        <li>
          <Button color="link" size="sm" onClick={onDelete}>
            <i className="bi bi-trash" />Delete
          </Button>
        </li>
      }
      </ul>
      </>
  );
}
